import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NAV_LINKS } from '../../../data/navigation';
import { CONTACT } from '../../../constants';
import { useTheme } from '../../../hooks/useTheme';
import './Header.css';

function NavLink({ link, className, onClick }) {
  if (link.router) {
    return (
      <Link to={link.href} className={className} onClick={onClick}>
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href} className={className} onClick={onClick}>
      {link.label}
    </a>
  );
}

const IconMoon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const IconSun = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>

        {/* Logo */}
        <a href="/" className="site-header__logo">
          <img
            src="/NimbleShortLet.png"
            alt="NimbleShortLet Logo"
            className="site-header__logo-img"
          />
        </a>

        {/* Right group: nav + actions + theme toggle + burger */}
        <div className="site-header__end">

          {/* Nav + phone + CTA — desktop only */}
          <div className="site-header__right">
            <nav className="site-nav" aria-label="Main navigation">
              {NAV_LINKS.map((link) => (
                <NavLink key={link.href} link={link} className="site-nav__link" />
              ))}
            </nav>

            <div className="site-header__actions">
              <a href={CONTACT.phoneHref} className="site-header__phone">
                <span className="site-header__phone-label">Call Us</span>
                <span className="site-header__phone-num">{CONTACT.phone}</span>
              </a>
              <a href="/#contact" className="site-header__cta">
                Get in Touch
              </a>
            </div>
          </div>

          {/* Theme toggle — always visible */}
          <button
            className="theme-toggle"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>

          {/* Hamburger — mobile only */}
          <button
            className={`site-header__burger${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {menuOpen && (
        <div
          className="mobile-nav"
          onClick={closeMenu}
          aria-modal="true"
          role="dialog"
          aria-label="Navigation menu"
        >
          <div className="mobile-nav__panel" onClick={(e) => e.stopPropagation()}>
            <nav className="mobile-nav__links" aria-label="Mobile navigation">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.href}
                  link={link}
                  className="mobile-nav__link"
                  onClick={closeMenu}
                />
              ))}
            </nav>

            <div className="mobile-nav__footer">
              <a href={CONTACT.phoneHref} className="mobile-nav__phone" onClick={closeMenu}>
                <span className="mobile-nav__phone-label">Call Us</span>
                <span className="mobile-nav__phone-num">{CONTACT.phone}</span>
              </a>
              <a href="/#contact" className="mobile-nav__cta" onClick={closeMenu}>
                Get in Touch →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
